<template>
  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="PTRI"
        for="pulse_train_repetition_interval"
        v-show="isThirdLevelTimingParameters && isSecondLevelTimingParameters"
        filled
        dense
        label="PTR Interval [ms]"
        type="number"
        step="0.000001"
        :min="pulse_train_duration_ms"
        :rules="[
          val => val > 0 || 'Value must be > 0',
          () => ptrd_gteq_ptri || 'Pulse Train Repetition Duration must be larger than or equal to Pulse Train Repetition Interval',
          () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
          () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
          () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
        ]"
        v-model="pulse_train_repetition_interval_ms"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Total period of pulse train including off-time between two pulse trains; inverse of pulse train repetition
                frequency. Must be equal to (i.e., single pulse) or higher than pulse repetition interval. Must be a
                multiple of pulse repetition interval.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
      <q-input
        class="col"
        :dark="isDarkModeGlobal"
        ref="TrainRepetitionFrequency"
        for="pulse_train_repetition_frequency"
        v-show="isThirdLevelTimingParameters && isSecondLevelTimingParameters"
        filled
        dense
        label="PTR Frequency [Hz]"
        min="0"
        type="number"
        step="0.000001"
        v-model="pulse_train_repetition_frequency_Hz"
        :rules="[
          val => val > 0 || 'Value must be > 0',
          () => ptrd_gteq_ptri || 'Pulse Train Repetition Duration must be larger than or equal to Pulse Train Repetition Interval',
          () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
          () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
          () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
        ]"
        @update:modelValue="PulseTrainReptitionIntervalUpdateByRepetitionFrequency"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Pulse Train Repetition Frequency = 1 / Pulse Train Repetition Interval. Total period of pulse train including ramping and off-time between two pulse trains; inverse of pulse train repetition interval. The pulse train repetition interval should always be equal or larger than the pulse train duration.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>

  <q-input
    :dark="isDarkModeGlobal"
    ref="PTRD"
    for="ptr_duration"
    v-show="isThirdLevelTimingParameters && isSecondLevelTimingParameters"
    filled
    dense
    label="PTR Duration [ms]"
    type="number"
    step="0.000001"
    :min="pulse_train_repetition_interval_ms"
    :max="18_000_000"
    v-model="pulse_train_repetition_duration_ms"
    :rules="[
      val => val > 0 || 'Value must be > 0',
      () => ptRepDur_isValid || `Pulse Train Repetition Duration max is ${pulse_train_repetition_duration_max}ms
      (${pulse_train_repetition_duration_max/600000} minutes)`,
      () => ptrd_gteq_ptri || 'Pulse Train Repetition Duration must be larger than or equal to Pulse Train Repetition Interval',
      () => ptd_gteq_pri || 'Pulse Train Duration must be larger than or equal to Pulse Repetition Interval',
      () => ptri_gteq_ptd || 'Pulse Train Repetition Interval must be larger than or equal to Pulse Train Duration',
      () => pri_gteq_pd || 'Pulse Repetition Interval must be larger than or equal to Pulse Duration',
    ]"
    @update:modelValue="timingGraphRoundDown"

    hide-bottom-space
    hide-hint
    :hint="pulse_train_repetition_duration_ms_hint"
    @blur="updateSeen = true"
  >
    <template #append>
      <q-btn
        icon="help"
        color="primary"
        size="sm"
        dense
        flat
      >
        <q-tooltip>
          Show note
        </q-tooltip>
        <q-popup-proxy :offset="[-70, 20]">
          <q-banner :dark="isDarkModeGlobal">
            <template #avatar>
              <q-icon
                name="help"
                color="primary"
              />
            </template>
            Duration for which pulse trains are repeated.
          </q-banner>
        </q-popup-proxy>
      </q-btn>
    </template>
  </q-input>

  <div>
    <div class="row q-gutter-xs">
      <q-input
        class="col"
        ref="PTRaD"
        for="ptr_ramp_duration"
        :dark="isDarkModeGlobal"
        v-show="isThirdLevelTimingParameters && timingParameters && isSecondLevelTimingParameters"
        filled
        dense
        label="PTR Ramp Duration [ms]"
        type="number"
        step="0.000001"
        :min="0"
        :max="pulse_train_repetition_ramp_duration_limit"
        v-model="pulse_train_repetition_ramp_duration_ms"
        :rules="[
          () => ptrrd_isValid || 'Pulse Train Repetition Ramp Duration should always be equal or less than Pulse Train Repetition Duration minus all off signal at the end of the Pulse train repetition divided by 2.',
        ]"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Time from onset of pulse train repetition to attainment of maximum pressure within the pulse train
                repetition.
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>

      <q-input
        class="col"
        ref="PPTRRW"
        for="percentage_of_ramp_width"
        :dark="isDarkModeGlobal"
        v-show="isThirdLevelTimingParameters && timingParameters && isSecondLevelTimingParameters"
        filled
        dense
        label="% of PTR Ramp Width"
        type="number"
        step="0.000001"
        :min="0"
        :max="50"
        v-model="percentage_pulse_train_repetition_ramp_width"
        :rules="[
          (val) => val <= 50 || 'To prevent overlapping up and down ramping, the percentage of ramping is cut off at 50%'
        ]"
        @blur="updatePulseTrainRepetitionRampDuration"
        hide-bottom-space
      >
        <template #append>
          <q-btn
            icon="help"
            color="primary"
            size="sm"
            dense
            flat
          >
            <q-tooltip>
              Show note
            </q-tooltip>
            <q-popup-proxy :offset="[-70, 20]">
              <q-banner :dark="isDarkModeGlobal">
                <template #avatar>
                  <q-icon
                    name="help"
                    color="primary"
                  />
                </template>
                Pulse Train Repetition Ramp Duration can be calculated based on the percentage of the Pulse Train Repetition Ramp Width.
                Pulse Train Repetition Ramp Width = PTRD - (PTRI - PTD) - (PRI - PD) = Pulse Train Repetition Duration - (Pulse Train Repetition Interval - Pulse Train Duration) - (Pulse Repetition Interval - Pulse Duration)
              </q-banner>
            </q-popup-proxy>
          </q-btn>
        </template>
      </q-input>
    </div>
  </div>

  <q-select
    :dark="isDarkModeGlobal"
    v-show="isThirdLevelTimingParameters && timingParameters && isSecondLevelTimingParameters"
    label="PTR Ramp Shape"
    filled
    dense
    type="text"
    :options="rampShapes"
    v-model="pulse_train_repetition_ramp_shape"
  >
    <template #append>
      <q-btn
        icon="help"
        color="primary"
        size="sm"
        dense
        flat
      >
        <q-tooltip>
          Show note
        </q-tooltip>
        <q-popup-proxy :offset="[-70, 20]">
          <q-banner :dark="isDarkModeGlobal">
            <template #avatar>
              <q-icon
                name="help"
                color="primary"
              />
            </template>
            For example Linear or Tukey.
          </q-banner>
        </q-popup-proxy>
      </q-btn>
    </template>
  </q-select>

  <q-input
    v-show="isThirdLevelTimingParameters && isSecondLevelTimingParameters"
    class="col"
    :dark="isDarkModeGlobal"
    ref="DutyCycleOfPTR"
    for="DutyCycleOfPTR"
    outlined
    readonly
    dense
    label="Duty Cycle of PTR"
    type="number"
    step="0.000001"
    min="0"
    v-model="calculatorStore.duty_cycle_of_pulse_train_repetition"
    hide-bottom-space
  />
</template>
<script setup lang="ts">
import {computed, ref, watch} from "vue"
import {storeToRefs} from "pinia"
import {useCalculatorStore} from '../stores/calculator.store'
import {formatNumber} from "./utils";

const calculatorStore = useCalculatorStore()
const {
  timingParameters,
  isSecondLevelTimingParameters,
  isThirdLevelTimingParameters,
  pulse_duration_ms,
  pulse_repetition_interval_ms,
  pulse_train_duration_ms,
  pulse_train_repetition_interval_ms,
  pulse_train_repetition_duration_ms,
  pulse_train_repetition_ramp_shape,
  pulse_train_repetition_ramp_duration_ms,
  time_parameters_valid,
  pulse_train_repetition_duration_max,
  isDarkModeGlobal,
  rampShapes,
  percentage_pulse_train_repetition_ramp_width,
  updateSeenTrainSection,
  importingData
} = storeToRefs(calculatorStore)

const PTRD = ref(null)
const PTRaD = ref(null)
const PTRI = ref(null)
const PPTRRW = ref(null)
const TrainRepetitionFrequency = ref(null)

let oldPulse_train_repetition_duration_ms = ref(null)
const updateSeen = ref(false)
const pulse_train_repetition_duration_ms_hint = computed(() => !updateSeen.value && oldPulse_train_repetition_duration_ms.value !== null
    ? `This timing parameter has been rounded down to obtain complete pulses and/or pulse trains for smooth ramping
     and realistic US protocols. Pulse train repetition duration was rounded down from
${oldPulse_train_repetition_duration_ms.value} to ${pulse_train_repetition_duration_ms.value}`: '')

const division = computed(() => pulse_train_repetition_duration_ms.value / pulse_train_repetition_interval_ms.value)
const roundDown = computed(() => !Number.isInteger(division.value))
const multiplier = computed(() => Math.floor(division.value))
const pulse_train_repetition_frequency_Hz = ref(formatNumber(1 / (pulse_train_repetition_interval_ms.value / 1000)))


// RULES
// Don't allow a Pulse Train Repetition Interval smaller than Pulse Train Duration (equal is allowed).
const ptri_gteq_ptd = computed(() => +pulse_train_repetition_interval_ms.value >= +pulse_train_duration_ms.value)
// The Pulse Repetition Interval should always be larger than the Pulse Duration.
const pri_gteq_pd = computed(() => +pulse_repetition_interval_ms.value >= +pulse_duration_ms.value)
// Don't allow a Pulse Train Duration smaller than the Pulse Repetition Interval (equal is allowed).
const ptd_gteq_pri = computed(() => +pulse_train_duration_ms.value >= +pulse_repetition_interval_ms.value)
// Don't allow a Pulse Train Repetition Duration smaller than the Pulse Train Repetition Interval (equal is allowed).
const ptrd_gteq_ptri = computed(() => +pulse_train_repetition_duration_ms.value >= +pulse_train_repetition_interval_ms.value)

const ptRepDurMax = 18_000_000
const ptRepDur_isValid = computed(() => +pulse_train_repetition_duration_ms.value <= ptRepDurMax)
const pulseOffTime = computed(() => pulse_repetition_interval_ms.value - pulse_duration_ms.value)
const pulseTrainOffTime = computed(() => pulse_train_repetition_interval_ms.value - pulse_train_duration_ms.value)
const pulse_train_repetition_ramp_duration_limit = computed(() => (+pulse_train_repetition_duration_ms.value - pulseOffTime.value - pulseTrainOffTime.value) / 2)
const ptrrd_isValid = computed(() => +pulse_train_repetition_ramp_duration_ms.value <= pulse_train_repetition_ramp_duration_limit.value)
const pulseTrainRepetitionRampWidth = computed(() => {
  return pulse_train_repetition_duration_ms.value - (pulse_train_repetition_interval_ms.value - pulse_train_duration_ms.value)
  - (pulse_repetition_interval_ms.value - pulse_duration_ms.value)
})

function PulseTrainReptitionIntervalUpdateByRepetitionFrequency() {
  if (+pulse_train_repetition_frequency_Hz.value > 0) {
    pulse_train_repetition_interval_ms.value = formatNumber((1 / pulse_train_repetition_frequency_Hz.value) * 1000)
    calculatorStore.showRecalculateButton()
  }
}

function timingGraphRoundDown() {
  updateSeen.value = false
  if (roundDown.value) {
    oldPulse_train_repetition_duration_ms.value = pulse_train_repetition_duration_ms.value
    const newValue = +pulse_train_repetition_interval_ms.value * multiplier.value
    pulse_train_repetition_duration_ms.value = Number.isInteger(newValue) ? newValue : formatNumber(newValue)
  }
  calculatorStore.showRecalculateButton()
}

function updatePulseTrainRepetitionRampDuration() {
  if (percentage_pulse_train_repetition_ramp_width.value > 50) {
    percentage_pulse_train_repetition_ramp_width.value = 50
  }
  if (!importingData.value) {
    pulse_train_repetition_ramp_duration_ms.value = formatNumber((percentage_pulse_train_repetition_ramp_width.value / 100) * pulseTrainRepetitionRampWidth.value)
  }
  calculatorStore.showRecalculateButton()
}

watch(time_parameters_valid, (bool) => {
  if (bool) {
    PTRD.value.resetValidation()
    PTRaD.value.resetValidation()
    PTRI.value.resetValidation()
    TrainRepetitionFrequency.value.resetValidation()
    PPTRRW.value.resetValidation()
  }
})

// The pulse train repetition duration default to the pulse train repetition interval
watch(pulse_train_repetition_interval_ms, (newVal) => {
  if (+newVal > 0) {
    pulse_train_repetition_frequency_Hz.value = formatNumber(1 / (+newVal / 1000))
    if (+newVal >= pulse_train_repetition_duration_ms.value) {
      pulse_train_repetition_duration_ms.value = +newVal
    }
    timingGraphRoundDown()
    calculatorStore.showRecalculateButton()
    updateSeenTrainSection.value = false
  }
})

watch(pulse_train_repetition_ramp_shape, () => calculatorStore.showRecalculateButton())
watch(pulseTrainRepetitionRampWidth, updatePulseTrainRepetitionRampDuration)
watch(pulse_train_repetition_ramp_duration_ms, () => {
  percentage_pulse_train_repetition_ramp_width.value = formatNumber((pulse_train_repetition_ramp_duration_ms.value / pulseTrainRepetitionRampWidth.value) * 100)
  calculatorStore.showRecalculateButton()
})
</script>